import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, ChevronRight, Zap, ArrowRight, Check, Star, AlertTriangle } from 'lucide-react';

const METHODS = ['Bitcoin', 'E-wallet', 'Credit Card', 'Bank Transfer'];

export default function WithdrawalPredictor() {
  const [casinoSlug, setCasinoSlug] = useState('bitstarz');
  const [method, setMethod] = useState('Bitcoin');
  const [amount, setAmount] = useState('100');
  const [casinos, setCasinos] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/tools/withdrawal-casinos').then(r => setCasinos(r.data.casinos)).catch(() => {}); }, []);

  const handlePredict = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tools/withdrawal-prediction', { casino_slug: casinoSlug, method, amount: parseFloat(amount) });
      setResult(res.data);
    } catch (err) { toast.error('Prediction failed'); }
    finally { setLoading(false); }
  };

  const formatTime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${Math.round(hours)} hrs`;
    const days = Math.round(hours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="withdrawal-predictor-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Withdrawal Predictor</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/15 flex items-center justify-center shrink-0"><Clock className="h-6 w-6 text-[#8b5cf6]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Withdrawal Timeline Predictor</h1><p className="text-[#a0a8b8] text-sm mt-1">Estimate real-world withdrawal time based on casino and payment method</p></div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0 max-w-2xl">
          <div className="ciq-card mb-6">
            <form onSubmit={handlePredict} className="space-y-5">
              <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Select Casino</label>
                <Select value={casinoSlug} onValueChange={setCasinoSlug}>
                  <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="wd-casino-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#252b3b] border-white/10 max-h-[300px]">{casinos.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}{c.is_partner ? ' \u2B50 Recommended' : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Withdrawal Method</label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="wd-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#252b3b] border-white/10">{METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Withdrawal Amount</label>
                <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} required min="1"
                    className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="wd-amount-input" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#8b5cf6] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="wd-predict-btn">
                {loading ? 'Predicting...' : 'Predict Timeline'}
              </button>
            </form>
          </div>
          {result && result.found && (
            <div className="animate-slide-up space-y-4">
              <div className="ciq-card border-[#8b5cf6]/30">
                <p className="text-xs text-[#a0a8b8] mb-1">Expected Arrival</p>
                <p className="text-2xl font-bold text-white">{formatTime(result.min_hours)} - {formatTime(result.max_hours)}</p>
                <p className="text-xs text-[#64748b]">{result.casino_name} via {result.method}</p>
              </div>
              {/* Timeline */}
              <div className="ciq-card">
                <h3 className="text-white font-semibold text-sm mb-4">Timeline</h3>
                <div className="space-y-4 pl-4 border-l-2 border-[#4d7cfe]/30">
                  {result.timeline.map((step, i) => (
                    <div key={i} className="relative pl-6">
                      <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${step.is_arrival ? 'bg-[#10b981] border-[#10b981]' : 'bg-[#252b3b] border-[#4d7cfe]'}`} />
                      <p className={`text-sm ${step.is_arrival ? 'text-[#10b981] font-semibold' : 'text-white'}`}>{step.label}</p>
                      <p className="text-xs text-[#64748b]">{formatTime(step.hours)}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Comparison */}
              <div className="ciq-card">
                <h3 className="text-white font-semibold text-sm mb-3">Casino Comparison ({result.method})</h3>
                <div className="space-y-1.5">
                  {result.comparisons.map((c, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${c.is_current ? 'bg-[#4d7cfe]/10 border border-[#4d7cfe]/20' : c.is_partner ? 'bg-[#f59e0b]/5 border border-[#f59e0b]/15' : ''}`} data-testid={`comparison-row-${i}`}>
                      <span className={`flex items-center gap-1.5 ${c.is_current ? 'text-[#4d7cfe] font-medium' : c.is_partner ? 'text-[#f59e0b] font-medium' : 'text-[#a0a8b8]'}`}>
                        {c.is_partner && <Star className="h-3.5 w-3.5 text-[#f59e0b] fill-[#f59e0b]" />}
                        {c.casino}
                      </span>
                      <span className={c.is_current ? 'text-[#4d7cfe] font-bold' : 'text-white'}>{formatTime(c.avg_hours)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="ciq-card">
                <h3 className="text-white font-semibold text-sm mb-2">Speed Tips</h3>
                <ul className="space-y-2">{result.tips.map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><Zap className="h-4 w-4 text-[#f59e0b] shrink-0 mt-0.5" />{t}</li>)}</ul>
              </div>
            </div>
          )}
          {result && !result.found && (
            <div className="ciq-card text-center py-8 animate-slide-up" data-testid="wd-method-unavailable">
              <AlertTriangle className="h-8 w-8 text-[#f59e0b] mx-auto mb-3" />
              <p className="text-[#f59e0b] text-sm font-medium">{result.message}</p>
              <p className="text-xs text-[#64748b] mt-2">Try a different withdrawal method for this casino.</p>
            </div>
          )}
        </div>
        <CasinoSidebar sourcePage="withdrawal-predictor" />
      </div>
    </div>
  );
}
